import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, Typography, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { UserProfile, SocialLinks } from '../../types/UserProfile';
import { SOCIAL_PLATFORMS } from '../../constants/socialPlatform';

interface ProfileEditFormProps {
  profile: UserProfile;
  onSave: (updatedProfile: UserProfile) => Promise<void>;
}

const ProfileEditForm: React.FC<ProfileEditFormProps> = ({ profile, onSave }) => {
  const { t } = useTranslation('settings');
  const [formData, setFormData] = useState(profile);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [ageWarning, setAgeWarning] = useState<string | null>(null);
  const [socialLinks, setSocialLinks] = useState<SocialLinks>(profile.social_links || {});

  useEffect(() => {
    setFormData(profile);
    setSocialLinks(profile.social_links || {});
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'birth_date' && value) {
      const { age } = validateAge(value);
      if (age < 13) {
        setAgeWarning(t('profile.ageWarning'));
      } else {
        setAgeWarning(null);
      }
    }
  };

  const handleSocialLinkChange = (key: keyof SocialLinks, value: string) => {
    setSocialLinks(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  // Nintendo Switch用のハンドラー（数字のみ、自動ハイフン挿入）
  const handleSwitchCodeChange = (value: string) => {
    // 数字のみを抽出
    const numbers = value.replace(/\D/g, '');

    // 12桁まで制限
    const limited = numbers.slice(0, 12);

    // 4桁ごとにハイフンを挿入
    let formatted = '';
    for (let i = 0; i < limited.length; i++) {
      if (i > 0 && i % 4 === 0) {
        formatted += '-';
      }
      formatted += limited[i];
    }

    // プレフィックスなしで保存
    setSocialLinks(prev => ({
      ...prev,
      nsw: formatted || undefined
    }));
  };

  // YouTube用のハンドラー（@なしで保存）
  const handleYouTubeChange = (value: string) => {
    // @を除いた値を取得してそのまま保存
    const cleanValue = value.replace(/^@/, '');

    setSocialLinks(prev => ({
      ...prev,
      yt: cleanValue || undefined
    }));
  };

  // Bluesky用のハンドラー（.bsky.socialなしで保存）
  const handleBlueskyChange = (value: string) => {
    // .bsky.socialを除いた値を取得
    const cleanValue = value.replace(/\.bsky\.social$/, '');

    setSocialLinks(prev => ({
      ...prev,
      bsky: cleanValue || undefined
    }));
  };

  // Website用のハンドラー（https://なしで保存）
  const handleWebsiteChange = (value: string) => {
    // https:// や http:// を除いた値を取得
    let cleanValue = value.replace(/^https?:\/\//, '');

    // 末尾のスラッシュを削除
    cleanValue = cleanValue.replace(/\/+$/, '');

    setSocialLinks(prev => ({
      ...prev,
      web: cleanValue || undefined
    }));
  };

  const validateAge = (birthDate: string): { isValid: boolean; age: number } => {
    const today = new Date();
    const birth = new Date(birthDate);
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return { isValid: false, age: age - 1 };
    }
    return { isValid: true, age };
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.birth_date) {
      newErrors.birth_date = t('profile.birthDateRequired');
      setValidationErrors(newErrors);
      return false;
    }

    const { age } = validateAge(formData.birth_date);

    if (age < 5) {
      newErrors.birth_date = t('profile.ageMin');
    } else if (age >= 100) {
      newErrors.birth_date = t('profile.ageMax');
    }
    
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (formData.birth_date) {
      const { age } = validateAge(formData.birth_date);
      if (age < 13) {
        setAgeWarning(t('profile.ageWarning'));
      }
    }
  }, [formData.birth_date]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // 空文字列を含むsocial_linksをクリーンアップ
      const cleanedSocialLinks: SocialLinks = {};
      Object.entries(socialLinks).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          cleanedSocialLinks[key as keyof SocialLinks] = value.trim();
        }
      });

      const updatedProfile = {
        ...profile,
        display_name: formData.display_name,
        bio: formData.bio,
        location: formData.location,
        birth_date: formData.birth_date,
        social_links: Object.keys(cleanedSocialLinks).length > 0 ? cleanedSocialLinks : null
      };

      await onSave(updatedProfile);
    } catch (error: any) {
      if (error.response && error.response.data) {
        const serverErrors = error.response.data;
        const newErrors: { [key: string]: string } = {};
        Object.keys(serverErrors).forEach(key => {
          newErrors[key] = serverErrors[key][0];
        });
        setErrors(newErrors);
      }
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: 600 }}>
      <TextField
        fullWidth
        size="small"
        margin="normal"
        label={t('profile.displayName')}
        name="display_name"
        value={formData.display_name}
        onChange={handleChange}
      />
      <TextField
        fullWidth
        size="small"
        margin="normal"
        label={t('profile.bio')}
        name="bio"
        value={formData.bio}
        onChange={handleChange}
        multiline
        rows={4}
      />
      <TextField
        fullWidth
        size="small"
        margin="normal"
        label={t('profile.location')}
        name="location"
        value={formData.location}
        onChange={handleChange}
      />
      <TextField
        fullWidth
        size="small"
        required
        margin="normal"
        label={t('profile.birthDate')}
        name="birth_date"
        type="date"
        value={formData.birth_date || ''}
        onChange={handleChange}
        error={Boolean(validationErrors.birth_date)}
        helperText={validationErrors.birth_date || ageWarning}
        FormHelperTextProps={{
          sx: {
            color: ageWarning ? 'warning.main' : (validationErrors.birth_date ? 'error.main' : 'inherit')
          }
        }}
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{
          min: new Date(new Date().setFullYear(new Date().getFullYear() - 100)).toISOString().split('T')[0]
        }}
      />
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontSize: {xs: 12, sm: 13}, lineHeight: '1.2' }}>
        {t('profile.birthDateNote')}
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontSize: '0.9rem', lineHeight: 1.2}}>{t('profile.platformLinks')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              {t('profile.platformLinksDesc')}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: -0.5 }}>{t('profile.web')}</Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2
            }}>
            {SOCIAL_PLATFORMS.filter(p => p.category === 'sns').map(platform => {
              // YouTube用の特別処理
              if (platform.key === 'yt') {
                return (
                  <TextField
                    key={platform.key}
                    fullWidth
                    size="small"
                    label={platform.label}
                    placeholder="channelname"
                    value={socialLinks.yt || ''}
                    onChange={(e) => handleYouTubeChange(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            {platform.icon}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.disabled',
                              userSelect: 'none',
                              fontSize: '0.875rem'
                            }}
                          >
                            @
                          </Typography>
                        </Box>
                      )
                    }}
                  />
                );
              }

              // Bluesky用の特別処理
              if (platform.key === 'bsky') {
                return (
                  <TextField
                    key={platform.key}
                    fullWidth
                    size="small"
                    label={platform.label}
                    placeholder="username"
                    value={socialLinks.bsky || ''}
                    onChange={(e) => handleBlueskyChange(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            {platform.icon}
                          </Box>
                        </Box>
                      ),
                      endAdornment: (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.disabled',
                            userSelect: 'none',
                            fontSize: '0.875rem'
                          }}
                        >
                          .bsky.social
                        </Typography>
                      )
                    }}
                  />
                );
              }

              // Website用の特別処理
              if (platform.key === 'web') {
                return (
                  <TextField
                    key={platform.key}
                    fullWidth
                    size="small"
                    label={platform.label}
                    placeholder="example.com"
                    value={socialLinks.web || ''}
                    onChange={(e) => handleWebsiteChange(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            {platform.icon}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.disabled',
                              userSelect: 'none',
                              fontSize: '0.875rem'
                            }}
                          >
                            https://
                          </Typography>
                        </Box>
                      )
                    }}
                  />
                );
              }

              // その他のSNS
              return (
                <TextField
                  key={platform.key}
                  fullWidth
                  size="small"
                  label={platform.label}
                  placeholder={platform.placeholder}
                  value={socialLinks[platform.key] || ''}
                  onChange={(e) => handleSocialLinkChange(platform.key, e.target.value)}
                  InputProps={{
                    startAdornment: <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>{platform.icon}</Box>
                  }}
                />
              );
            })}
            </Box>

            <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: -0.5, mt: 1 }}>{t('profile.gamingPlatform')}</Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2
            }}>
            {SOCIAL_PLATFORMS.filter(p => p.category === 'gaming').map(platform => {
              // Nintendo Switchの場合は専用処理
              if (platform.key === 'nsw') {
                return (
                  <TextField
                    key={platform.key}
                    fullWidth
                    size="small"
                    label={platform.label}
                    placeholder="1234-5678-9012"
                    value={socialLinks.nsw || ''}
                    onChange={(e) => handleSwitchCodeChange(e.target.value)}
                    inputProps={{
                      maxLength: 14, // 12桁 + 2ハイフン
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            {platform.icon}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.disabled',
                              userSelect: 'none',
                              fontSize: '0.875rem'
                            }}
                          >
                            SW-
                          </Typography>
                        </Box>
                      )
                    }}
                  />
                );
              }

              // その他のプラットフォーム
              return (
                <TextField
                  key={platform.key}
                  fullWidth
                  size="small"
                  label={platform.label}
                  placeholder={platform.placeholder}
                  value={socialLinks[platform.key] || ''}
                  onChange={(e) => handleSocialLinkChange(platform.key, e.target.value)}
                  InputProps={{
                    startAdornment: <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>{platform.icon}</Box>
                  }}
                />
              );
            })}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 3 }}>
        <Button type="submit" variant="contained" color="primary">
          {t('save', { ns: 'common' })}
        </Button>
      </Box>
      {Object.keys(errors).map((key) => (
        <Typography key={key} color="error" sx={{ mt: 2 }}>
          {errors[key]}
        </Typography>
      ))}
    </Box>
  );
};

export default ProfileEditForm;